/**
 * Pacote 5 (P5-04): aviso "Sua sessão vai expirar", 5 minutos antes do fim da
 * inatividade (120 min para todos os perfis desde 26/10/2026). Contagem
 * regressiva; "Continuar conectado" faz uma requisição normal (renova no
 * servidor) e "Sair agora" encerra a sessão. Fechar o aviso (Esc ou clique
 * fora) também é uso e vale como "Continuar conectado".
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '../comercial/ui.tsx';

/** "1 h 55 min", "12 min", "45 s" — a duração sem atividade. */
export function duracaoSemAtividade(ms: number): string {
  const min = Math.floor(Math.max(0, ms) / 60_000);
  if (min < 1) return `${Math.floor(Math.max(0, ms) / 1000)} s`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
}

/** "5 minutos", "4:59", "0:07" — o tempo até o encerramento. */
export function contagemRegressiva(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  if (total % 60 === 0 && total >= 60) return total === 60 ? '1 minuto' : `${total / 60} minutos`;
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function AvisoSessao({
  expiraEm,
  ultimaAtividade,
  absolutaHoras,
  aoContinuar,
  aoSair,
}: {
  /** Instante (Date.now) em que o servidor encerrará a sessão por inatividade. */
  expiraEm: number;
  /** Última atividade conhecida, nesta ou em outra aba. */
  ultimaAtividade: number;
  absolutaHoras: number;
  aoContinuar: () => void;
  aoSair: () => void;
}) {
  const [agora, setAgora] = useState(() => Date.now());
  const continuar = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const t = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  // o foco inicial vai para "Continuar conectado" (o Modal foca o primeiro botão; Enter não deve sair por engano)
  useEffect(() => {
    const t = window.setTimeout(() => continuar.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <Modal
      titulo="Sua sessão vai expirar"
      aoFechar={aoContinuar}
      medio
      sobreposto
      rodape={
        <>
          <button type="button" className="botao-secundario" data-teste="sair-agora" onClick={aoSair}>
            Sair agora
          </button>
          <button type="button" className="botao-primario" data-teste="continuar-conectado" ref={continuar} onClick={aoContinuar}>
            Continuar conectado
          </button>
        </>
      }
    >
      <div data-teste="aviso-sessao">
        <p>
          Nenhuma atividade sua há <strong>{duracaoSemAtividade(agora - ultimaAtividade)}</strong> nesta ou em outras abas do HUB. A sessão será encerrada em{' '}
          <strong data-teste="aviso-sessao-contagem">{contagemRegressiva(expiraEm - agora)}</strong>.
        </p>
        <p className="aviso aviso-info">
          Contam como uso: cliques, teclado, rolagem e toque, em qualquer aba aberta. Requisições automáticas (ex.: atualizações de tela) não renovam a sessão. O limite de{' '}
          {absolutaHoras} horas desde o login não muda; depois dele, é preciso entrar de novo.
        </p>
      </div>
    </Modal>
  );
}
